import {
  FileText, Truck, Calendar, Package, CheckCircle, Ship, AlertCircle 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  type PurchaseOrder,
  PO_STATUS_LABELS,
} from '@/types/orders';

interface PurchaseOrderCardProps {
  readonly order: PurchaseOrder;
  readonly pendingActionsCount?: number;
  readonly onAcknowledge?: (poId: string) => void;
  readonly onMarkShipped?: (poId: string) => void;
  readonly onViewDetails?: (poId: string) => void;
}

export function PurchaseOrderCard({
  order,
  pendingActionsCount = 0,
  onAcknowledge,
  onMarkShipped,
  onViewDetails,
}: PurchaseOrderCardProps) {
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: order.currency 
    }).format(amount);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const canAcknowledge = order.status === 'sent';
  const canMarkShipped = order.status === 'acknowledged';
  const isDelivered = order.status === 'delivered';
  const hasPendingActions = pendingActionsCount > 0;

  return (
    <Card className={`hover:shadow-sm transition-shadow border-slate-200 ${hasPendingActions ? 'ring-1 ring-amber-200' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${
              hasPendingActions ? 'bg-amber-50' : 'bg-slate-100'
            }`}>
              {hasPendingActions ? (
                <AlertCircle className="h-5 w-5 text-amber-600" />
              ) : (
                <FileText className="h-5 w-5 text-slate-500" />
              )}
            </div>
            <div>
              <CardTitle className="text-base text-slate-900">{order.poNumber}</CardTitle>
              <p className="text-sm text-slate-500">{order.deliverySiteName}</p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
              isDelivered ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'
            }`}>
              {PO_STATUS_LABELS[order.status]}
            </span>
            {hasPendingActions && (
              <span className="rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-xs font-medium">
                {pendingActionsCount} action{pendingActionsCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Truck className="h-4 w-4 text-slate-400" />
            <span className="text-slate-500">Delivery:</span>
            <span className="font-medium text-slate-700">{order.deliveryZone}</span>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            <span className="text-slate-500">Due:</span>
            <span className="font-medium text-slate-700">{formatDate(order.expectedDeliveryDate)}</span>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-3">
          <div className="flex items-center gap-2 mb-2">
            <Package className="h-4 w-4 text-slate-400" />
            <span className="text-sm font-medium text-slate-700">{order.lines.length} items</span>
          </div>
          <div className="space-y-1">
            {order.lines.slice(0, 3).map((line) => (
              <div key={line.lineId} className="flex justify-between text-sm">
                <span className="text-slate-500 truncate max-w-[200px]">{line.itemName}</span>
                <span className="text-slate-700">
                  {line.quantity} {line.unit}
                </span>
              </div>
            ))}
            {order.lines.length > 3 && (
              <p className="text-xs text-slate-400">+{order.lines.length - 3} more items</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
          <div>
            <p className="text-sm text-slate-500">Total Value</p>
            <p className="text-lg font-semibold text-slate-900">{formatCurrency(order.total)}</p>
          </div>
          <div className="flex gap-2">
            {canAcknowledge && onAcknowledge && (
              <Button size="sm" onClick={() => onAcknowledge(order.poId)} className="bg-slate-900 hover:bg-slate-800">
                <CheckCircle className="h-4 w-4 mr-1" />
                Acknowledge
              </Button>
            )}
            {canMarkShipped && onMarkShipped && (
              <Button size="sm" onClick={() => onMarkShipped(order.poId)} className="bg-slate-900 hover:bg-slate-800">
                <Ship className="h-4 w-4 mr-1" />
                Mark Shipped
              </Button>
            )}
            {onViewDetails && (
              <Button variant="outline" size="sm" onClick={() => onViewDetails(order.poId)} className="border-slate-200 text-slate-700">
                View Details
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
